/**
 * Logging Interceptor
 * Request/Response'ları loglar ve metrikleri toplar
 */

import { HttpRequestConfig, HttpResponse } from '../httpClient.interface';
import { isApiErrorResponse } from '../../types/api.types';
import { LOG_LEVEL, FEATURE_FLAGS } from '../../config/api.config';

/**
 * Log seviyeleri
 */
export enum LogLevel {
  DEBUG = 'debug',
  INFO = 'info',
  WARN = 'warn',
  ERROR = 'error'
}

/**
 * Log entry interface
 */
export interface LogEntry {
  timestamp: string;
  level: LogLevel;
  component: string;
  message: string;
  data?: any;
  correlationId?: string;
  requestId?: string;
  duration?: number;
  tags?: Record<string, string>;
}

/**
 * Request metrics
 */
export interface RequestMetrics {
  requestId: string;
  url: string;
  method: string;
  startTime: number;
  endTime?: number;
  duration?: number;
  status?: number;
  success?: boolean;
  requestSize?: number;
  responseSize?: number;
  cacheHit?: boolean;
  retryCount?: number;
}

/**
 * Performance threshold'ları
 */
export interface PerformanceThresholds {
  warning: number; // ms
  critical: number; // ms
}

/**
 * Logging interceptor interface
 */
export interface ILoggingInterceptor {
  logRequest(config: HttpRequestConfig): void;
  logResponse(response: HttpResponse, metrics: RequestMetrics): void;
  logError(error: any, metrics: RequestMetrics): void;
  logCustom(entry: LogEntry): void;
}

/**
 * Log storage interface
 */
export interface ILogStorage {
  save(entry: LogEntry): void;
  query(filter?: LogQueryFilter): LogEntry[];
  clear(): void;
  size(): number;
}

/**
 * Log query filter
 */
export interface LogQueryFilter {
  level?: LogLevel | LogLevel[];
  component?: string;
  startTime?: Date;
  endTime?: Date;
  correlationId?: string;
  tags?: Record<string, string>;
}

/**
 * Console Logging Strategy
 */
export class ConsoleLogger implements ILoggingInterceptor {
  private readonly component = 'API';
  private readonly performanceThresholds: PerformanceThresholds = {
    warning: 1000, // 1 second
    critical: 5000 // 5 seconds
  };

  logRequest(config: HttpRequestConfig): void {
    if (!this.shouldLog(LogLevel.DEBUG)) return;

    const logEntry: LogEntry = {
      timestamp: new Date().toISOString(),
      level: LogLevel.DEBUG,
      component: this.component,
      message: `Request: ${config.method?.toUpperCase()} ${config.url}`,
      data: this.sanitizeRequestData(config),
      requestId: config.headers?.['X-Request-ID'] as string,
      tags: {
        type: 'request',
        method: config.method || 'GET'
      }
    };

    this.printLog(logEntry);
  }

  logResponse(response: HttpResponse, metrics: RequestMetrics): void {
    const level = this.getLogLevelForResponse(response, metrics);
    if (!this.shouldLog(level)) return;

    const logEntry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      component: this.component,
      message: `Response: ${response.status} ${response.statusText}`,
      data: {
        status: response.status,
        statusText: response.statusText,
        url: metrics.url,
        method: metrics.method,
        duration: metrics.duration,
        cacheHit: metrics.cacheHit,
        retryCount: metrics.retryCount
      },
      requestId: metrics.requestId,
      duration: metrics.duration,
      tags: {
        type: 'response',
        method: metrics.method,
        status: response.status.toString(),
        success: response.status >= 200 && response.status < 300 ? 'true' : 'false'
      }
    };

    this.printLog(logEntry);

    // Performance warning log
    if (metrics.duration && metrics.duration > this.performanceThresholds.warning) {
      this.logPerformanceWarning(metrics);
    }
  }

  logError(error: any, metrics: RequestMetrics): void {
    if (!this.shouldLog(LogLevel.ERROR)) return;

    const errorData = this.extractErrorData(error);
    
    const logEntry: LogEntry = {
      timestamp: new Date().toISOString(),
      level: LogLevel.ERROR,
      component: this.component,
      message: `Error: ${errorData.message}`,
      data: {
        ...errorData,
        metrics
      },
      requestId: metrics.requestId,
      duration: metrics.duration,
      tags: {
        type: 'error',
        method: metrics.method,
        status: error.response?.status?.toString() || '0'
      }
    };

    this.printLog(logEntry);
  }

  logCustom(entry: LogEntry): void {
    if (!this.shouldLog(entry.level)) return;

    const enhancedEntry: LogEntry = {
      ...entry,
      timestamp: entry.timestamp || new Date().toISOString(),
      component: entry.component || this.component
    };

    this.printLog(enhancedEntry);
  }

  // ==================== PRIVATE METHODS ====================

  private shouldLog(level: LogLevel): boolean {
    if (!FEATURE_FLAGS.ENABLE_REQUEST_LOGGING) return false;

    const levelPriority = {
      [LogLevel.DEBUG]: 0,
      [LogLevel.INFO]: 1,
      [LogLevel.WARN]: 2,
      [LogLevel.ERROR]: 3
    };

    const currentLevel = LOG_LEVEL as LogLevel;
    return levelPriority[level] >= levelPriority[currentLevel];
  }

  private printLog(entry: LogEntry): void {
    const style = this.getLogStyle(entry.level);
    const timestamp = new Date(entry.timestamp).toLocaleTimeString();
    
    const prefix = `[${timestamp}] [${entry.component}] [${entry.level.toUpperCase()}]`;
    const message = entry.message;
    
    switch (entry.level) {
      case LogLevel.DEBUG:
        console.debug(`%c${prefix} ${message}`, style, entry.data || '');
        break;
      case LogLevel.INFO:
        console.info(`%c${prefix} ${message}`, style, entry.data || '');
        break;
      case LogLevel.WARN:
        console.warn(`%c${prefix} ${message}`, style, entry.data || '');
        break;
      case LogLevel.ERROR:
        console.error(`%c${prefix} ${message}`, style, entry.data || '');
        break;
    }
  }

  private getLogStyle(level: LogLevel): string {
    const styles = {
      [LogLevel.DEBUG]: 'color: #666; font-weight: normal;',
      [LogLevel.INFO]: 'color: #2196F3; font-weight: bold;',
      [LogLevel.WARN]: 'color: #FF9800; font-weight: bold;',
      [LogLevel.ERROR]: 'color: #F44336; font-weight: bold;'
    };
    
    return styles[level] || styles[LogLevel.INFO];
  }

  private sanitizeRequestData(config: HttpRequestConfig): any {
    const sanitized: any = {
      url: config.url,
      method: config.method,
      headers: this.sanitizeHeaders(config.headers),
      params: config.params
    };

    // Sensitive data'ları maskele
    if (config.data) {
      if (config.data instanceof FormData) {
        sanitized.data = '[FormData]';
      } else if (typeof config.data === 'object') {
        sanitized.data = this.maskSensitiveFields(config.data);
      } else {
        sanitized.data = config.data;
      }
    }

    return sanitized;
  }

  private sanitizeHeaders(headers: any): any {
    if (!headers) return {};
    
    const sanitized: any = { ...headers };
    
    // Sensitive headers'ı maskele
    const sensitiveHeaders = ['authorization', 'x-csrf-token', 'x-xsrf-token', 'cookie'];
    
    for (const key in sanitized) {
      const lowerKey = key.toLowerCase();
      if (sensitiveHeaders.some(h => lowerKey.includes(h))) {
        sanitized[key] = '***';
      }
    }
    
    return sanitized;
  }

  private maskSensitiveFields(data: any): any {
    if (!data || typeof data !== 'object') return data;
    
    const sanitized = { ...data };
    const sensitiveFields = ['password', 'token', 'secret', 'key', 'creditcard', 'cvv'];
    
    for (const key in sanitized) {
      const lowerKey = key.toLowerCase();
      if (sensitiveFields.some(field => lowerKey.includes(field))) {
        sanitized[key] = '***';
      }
      
      // Recursive mask for nested objects
      if (sanitized[key] && typeof sanitized[key] === 'object') {
        sanitized[key] = this.maskSensitiveFields(sanitized[key]);
      }
    }
    
    return sanitized;
  }

  private getLogLevelForResponse(response: HttpResponse, metrics: RequestMetrics): LogLevel {
    if (response.status >= 500) {
      return LogLevel.ERROR;
    } else if (response.status >= 400) {
      return LogLevel.WARN;
    } else if (metrics.duration && metrics.duration > this.performanceThresholds.warning) {
      return LogLevel.WARN;
    } else {
      return LogLevel.INFO;
    }
  }

  private logPerformanceWarning(metrics: RequestMetrics): void {
    if (!this.shouldLog(LogLevel.WARN)) return;

    const logEntry: LogEntry = {
      timestamp: new Date().toISOString(),
      level: LogLevel.WARN,
      component: this.component,
      message: `Slow request: ${metrics.duration}ms`,
      data: {
        url: metrics.url,
        method: metrics.method,
        duration: metrics.duration,
        threshold: this.performanceThresholds.warning
      },
      requestId: metrics.requestId,
      tags: {
        type: 'performance',
        severity: metrics.duration && metrics.duration > this.performanceThresholds.critical 
          ? 'critical' 
          : 'warning'
      }
    };

    this.printLog(logEntry);
  }

  private extractErrorData(error: any): any {
    if (isApiErrorResponse(error.response?.data)) {
      return {
        message: error.response.data.message,
        status: error.response.data.status,
        error: error.response.data.error,
        validationErrors: error.response.data.validationErrors,
        code: error.response.data.code
      };
    }
    
    if (error.response) {
      return {
        message: error.message,
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data
      };
    }
    
    if (error.request) {
      return {
        message: 'No response received',
        code: error.code,
        isTimeout: error.code === 'ECONNABORTED'
      };
    }
    
    return {
      message: error.message || 'Unknown error'
    };
  }
}

/**
 * Memory Log Storage - Development için
 */
export class MemoryLogStorage implements ILogStorage {
  private logs: LogEntry[] = [];
  private readonly maxSize = 1000;

  save(entry: LogEntry): void {
    this.logs.push(entry);
    
    // Max size kontrolü
    if (this.logs.length > this.maxSize) {
      this.logs = this.logs.slice(-this.maxSize);
    }
  }

  query(filter?: LogQueryFilter): LogEntry[] {
    let filtered = [...this.logs];

    if (filter?.level) {
      const levels = Array.isArray(filter.level) ? filter.level : [filter.level];
      filtered = filtered.filter(log => levels.includes(log.level));
    }

    if (filter?.component) {
      filtered = filtered.filter(log => log.component === filter.component);
    }

    if (filter?.startTime) {
      filtered = filtered.filter(log => new Date(log.timestamp) >= filter.startTime!);
    }

    if (filter?.endTime) {
      filtered = filtered.filter(log => new Date(log.timestamp) <= filter.endTime!);
    }

    if (filter?.correlationId) {
      filtered = filtered.filter(log => log.correlationId === filter.correlationId);
    }

    if (filter?.tags) {
      filtered = filtered.filter(log => 
        Object.entries(filter.tags!).every(([key, value]) => 
          log.tags?.[key] === value
        )
      );
    }

    return filtered;
  }

  clear(): void {
    this.logs = [];
  }

  size(): number {
    return this.logs.length;
  }
}

/**
 * LocalStorage Log Storage - Production için
 */
export class LocalStorageLogStorage implements ILogStorage {
  private readonly STORAGE_KEY = 'api_logs';
  private readonly MAX_SIZE = 500;

  save(entry: LogEntry): void {
    const logs = this.getAllLogs();
    logs.push(entry);
    
    // Max size kontrolü
    if (logs.length > this.MAX_SIZE) {
      logs.splice(0, logs.length - this.MAX_SIZE);
    }
    
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(logs));
  }

  query(filter?: LogQueryFilter): LogEntry[] {
    const logs = this.getAllLogs();
    
    // MemoryLogStorage ile aynı filter logic
    const memoryStorage = new MemoryLogStorage();
    for (const log of logs) {
      memoryStorage.save(log);
    }
    
    return memoryStorage.query(filter);
  }

  clear(): void {
    localStorage.removeItem(this.STORAGE_KEY);
  }

  size(): number {
    return this.getAllLogs().length;
  }

  private getAllLogs(): LogEntry[] {
    try {
      const logsJson = localStorage.getItem(this.STORAGE_KEY);
      return logsJson ? JSON.parse(logsJson) : [];
    } catch (error) {
      console.error('Failed to parse logs from localStorage:', error);
      return [];
    }
  }
}

/**
 * Metrics Collector
 */
export class MetricsCollector {
  private readonly metrics: Map<string, RequestMetrics> = new Map();
  private readonly RETENTION_PERIOD = 5 * 60 * 1000; // 5 dakika

  startRequest(config: HttpRequestConfig): string {
    const requestId = config.headers?.['X-Request-ID'] as string || this.generateRequestId();
    
    const metrics: RequestMetrics = {
      requestId,
      url: config.url || '',
      method: config.method || 'GET',
      startTime: Date.now(),
      retryCount: config.retryCount || 0
    };

    this.metrics.set(requestId, metrics);
    this.cleanupOldMetrics();
    
    return requestId;
  }

  endRequest(requestId: string, response: HttpResponse): RequestMetrics {
    const metrics = this.metrics.get(requestId);
    
    if (!metrics) {
      return this.createDefaultMetrics(requestId);
    }

    metrics.endTime = Date.now();
    metrics.duration = metrics.endTime - metrics.startTime;
    metrics.status = response.status;
    metrics.success = response.status >= 200 && response.status < 300;
    
    // Response size estimation
    try {
      const responseString = JSON.stringify(response.data);
      metrics.responseSize = new Blob([responseString]).size;
    } catch {
      // Ignore size calculation errors
    }

    return metrics;
  }

  endRequestWithError(requestId: string, error: any): RequestMetrics {
    const metrics = this.metrics.get(requestId);
    
    if (!metrics) {
      return this.createDefaultMetrics(requestId);
    }

    metrics.endTime = Date.now();
    metrics.duration = metrics.endTime - metrics.startTime;
    metrics.status = error.response?.status || 0;
    metrics.success = false;

    return metrics;
  }

  getMetrics(requestId: string): RequestMetrics | undefined {
    return this.metrics.get(requestId);
  }

  getAllMetrics(): RequestMetrics[] {
    return Array.from(this.metrics.values());
  }

  clear(): void {
    this.metrics.clear();
  }

  private generateRequestId(): string {
    return `req_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }

  private createDefaultMetrics(requestId: string): RequestMetrics {
    return {
      requestId,
      url: '',
      method: 'GET',
      startTime: Date.now(),
      endTime: Date.now(),
      duration: 0,
      status: 0,
      success: false
    };
  }

  private cleanupOldMetrics(): void {
    const now = Date.now();
    
    for (const [requestId, metrics] of this.metrics.entries()) {
      if (metrics.startTime < now - this.RETENTION_PERIOD) {
        this.metrics.delete(requestId);
      }
    }
  }
}

/**
 * Logging Interceptor Manager
 */
export class LoggingInterceptorManager implements ILoggingInterceptor {
  private readonly loggers: ILoggingInterceptor[] = [];
  private readonly metricsCollector = new MetricsCollector();
  private readonly logStorage: ILogStorage;

  constructor() {
    // Default logger'ları ekle
    this.loggers.push(new ConsoleLogger());
    
    // Storage seçimi
    this.logStorage = import.meta.env.NODE_ENV === 'production' 
      ? new LocalStorageLogStorage()
      : new MemoryLogStorage();
  }

  addLogger(logger: ILoggingInterceptor): void {
    this.loggers.push(logger);
  }

  removeLogger(logger: ILoggingInterceptor): void {
    const index = this.loggers.indexOf(logger);
    if (index > -1) {
      this.loggers.splice(index, 1);
    }
  }

  logRequest(config: HttpRequestConfig): void {
    if (!FEATURE_FLAGS.ENABLE_REQUEST_LOGGING) return;

    const requestId = this.metricsCollector.startRequest(config);
    
    for (const logger of this.loggers) {
      try {
        logger.logRequest(config);
      } catch (error) {
        console.error('Logger failed:', error);
      }
    }

    // Log storage'a kaydet
    this.logStorage.save({
      timestamp: new Date().toISOString(),
      level: LogLevel.INFO,
      component: 'API',
      message: `Request started: ${config.method?.toUpperCase()} ${config.url}`,
      requestId,
      tags: {
        type: 'request_start',
        method: config.method || 'GET'
      }
    });
  }

  logResponse(response: HttpResponse, config: HttpRequestConfig): void {
    if (!FEATURE_FLAGS.ENABLE_RESPONSE_LOGGING) return;

    const requestId = config.headers?.['X-Request-ID'] as string;
    const metrics = this.metricsCollector.endRequest(requestId, response);
    
    for (const logger of this.loggers) {
      try {
        logger.logResponse(response, metrics);
      } catch (error) {
        console.error('Logger failed:', error);
      }
    }

    // Log storage'a kaydet
    this.logStorage.save({
      timestamp: new Date().toISOString(),
      level: response.status >= 400 ? LogLevel.WARN : LogLevel.INFO,
      component: 'API',
      message: `Request completed: ${response.status} ${metrics.duration}ms`,
      data: {
        status: response.status,
        duration: metrics.duration,
        url: metrics.url
      },
      requestId,
      duration: metrics.duration,
      tags: {
        type: 'request_end',
        method: metrics.method,
        status: response.status.toString(),
        success: metrics.success ? 'true' : 'false'
      }
    });
  }

  logError(error: any, config: HttpRequestConfig): void {
    if (!FEATURE_FLAGS.ENABLE_ERROR_REPORTING) return;

    const requestId = config.headers?.['X-Request-ID'] as string;
    const metrics = this.metricsCollector.endRequestWithError(requestId, error);
    
    for (const logger of this.loggers) {
      try {
        logger.logError(error, metrics);
      } catch (error) {
        console.error('Logger failed:', error);
      }
    }

    // Log storage'a kaydet
    this.logStorage.save({
      timestamp: new Date().toISOString(),
      level: LogLevel.ERROR,
      component: 'API',
      message: `Request failed: ${error.message || 'Unknown error'}`,
      data: this.extractErrorData(error),
      requestId,
      duration: metrics.duration,
      tags: {
        type: 'request_error',
        method: metrics.method,
        status: metrics.status?.toString() || '0'
      }
    });
  }

  logCustom(entry: LogEntry): void {
    for (const logger of this.loggers) {
      try{
        logger.logCustom(entry);
      } catch (error) {
        console.error('Logger failed:', error);
      }
    }

    this.logStorage.save(entry);
  }

  getLogStorage(): ILogStorage {
    return this.logStorage;
  }

  getMetricsCollector(): MetricsCollector {
    return this.metricsCollector;
  }

  clearLogs(): void {
    this.logStorage.clear();
    this.metricsCollector.clear();
  }

  private extractErrorData(error: any): any {
    if (error.response?.data) {
      return {
        status: error.response.status,
        data: error.response.data
      };
    }
    
    return {
      message: error.message,
      code: error.code
    };
  }
}

// Singleton instance
export const loggingInterceptorManager = new LoggingInterceptorManager();

// Default export
export default loggingInterceptorManager;